import { useLazyQuery, useQuery } from '@apollo/client';
import { GET_ME, GET_POSTS_BY_USER_ID, Me, Post } from '@frontend/data-access';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { ContentCardUser } from '@frontend/feature-content';
import { Button, Empty, Spin } from 'antd';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

interface cursorBasedResponseType {
  edges: {
    cursor: string;
    node: Post;
  }[];
  pageInfo: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

function UserProfilePosts() {
  const { t } = useTranslation('common');
  const router = useRouter();
  const { data: meData } = useQuery<{ me: Me }>(GET_ME);
  const isInternalUser = useMemo(
    () => ['ADMIN', 'INTRAPRENEUR'].includes(meData?.me?.role as string),
    [meData]
  );
  const userId = router.query['id'] as string;
  const [autoFetch, setAutoFetch] = useState<boolean>(false);
  const [params, setParams] = useState<{
    userId: string;
    first: number;
    after?: string;
  }>({
    userId: userId,
    first: 6,
  });
  const [data, setData] = useState<cursorBasedResponseType>();

  const [getData, { loading }] = useLazyQuery<{
    userPosts: cursorBasedResponseType;
  }>(GET_POSTS_BY_USER_ID, {
    onCompleted: (res) => {
      const tempData = data?.edges || [];
      setData({
        edges: [...tempData, ...res.userPosts.edges],
        pageInfo: res.userPosts.pageInfo,
      });
    },
  });

  const handleLoadMore = () => {
    setParams({
      ...params,
      after: data?.edges[data.edges.length - 1].cursor,
    });
  };

  useEffect(() => {
    getData({
      variables: {
        ...params,
      },
    });
  }, [getData, params]);

  const Loader = () => {
    return (
      <div className="p-6 text-center">
        <Spin />
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="text-gray-900 text-sm font-semibold">
        {t('recent_posts')}
      </div>
      <InfiniteScroll
        dataLength={data?.edges.length || 0}
        next={handleLoadMore}
        hasMore={(autoFetch && data?.pageInfo.hasNextPage) || false}
        loader={<Loader />}
        className="lg:w-full w-full !overflow-visible"
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-4 lg:gap-4 lg:grid-cols-3 w-full">
          {data?.edges
            .filter((data) => data.node.isPublic || isInternalUser)
            .map((data, index) => (
              <ContentCardUser key={index} post={data.node} />
            ))}
        </div>
      </InfiniteScroll>
      {loading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader />
        </div>
      ) : (
        data?.edges.length === 0 && (
          <div className="h-[200px] flex justify-center items-center">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        )
      )}
      {!autoFetch && data?.pageInfo.hasNextPage && (
        <div className="text-center">
          <Button
            shape="round"
            loading={loading}
            onClick={() => setAutoFetch(true)}
          >
            {t('see_more')}
          </Button>
        </div>
      )}
    </div>
  );
}

export default UserProfilePosts;
